import * as React from "react";
import { useInView } from "react-intersection-observer";
import BottomBanner from "common/components/bottomBanner";
import AppInstallDialogTrigger from "app/main/appInstallDialog/trigger";
import Icon from "common/icons";
import PACKS from "common/icons/packs";
import { FormattedMessage } from "react-intl";
import {
  Wrapper,
  Content,
  Title,
  ReviewBoxList,
  ReviewBox,
  ReviewHeader,
  UserName,
  ReviewContent,
  AppDownLoadButtonWrapper,
  AppDownLoadButton,
  RightMessageArrowWrapper,
  LeftMessageArrowWrapper,
} from "./styledComponents";
import { px2rem } from "common/helpers/rem";

const AppDownLoadButtonNormal = AppDownLoadButton.withComponent(
  AppInstallDialogTrigger.Normal,
);
const AppDownLoadButtonUnImplement = AppDownLoadButton.withComponent(
  AppInstallDialogTrigger.UnImplement,
);

const MAX_REVIEW_EXAMPLE_COUNT = 4;

interface IAppDownLoadBannerProps {
  type?: "NORMAL" | "UNIMPLEMENT";
  from: string;
  schema?: string;
  onAppDownloadButtonClick?: () => void;
}

const AppDownLoadBanner = React.memo(
  ({ type = "NORMAL", schema, from }: IAppDownLoadBannerProps) => {
    const [order, setOrder] = React.useState(0);
    const appDownloadButton = React.useMemo(() => {
      return React.createElement(
        type === "NORMAL"
          ? AppDownLoadButtonNormal
          : AppDownLoadButtonUnImplement,
        {
          featureId: from,
          schema,
          children: (
            <FormattedMessage id="post_footer_app_download_banner_button" />
          ),
        },
      );
    }, [type, from, schema]);
    const [inViewRef, inViewState, inViewEntry] = useInView({
      threshold: 0,
    });
    React.useEffect(() => {
      if (!inViewState && inViewEntry) {
        setOrder(prevOrder => (prevOrder + 1) % MAX_REVIEW_EXAMPLE_COUNT);
      }
    }, [inViewState]);
    const showOrder = order + 1;
    return (
      <Wrapper ref={inViewRef}>
        <BottomBanner height={px2rem(333)} />
        <Content>
          <Title>
            <FormattedMessage id="post_footer_app_download_banner_title" />
          </Title>
          <ReviewBoxList>
            <ReviewBoxComponent arrowDirection="right" order={showOrder} />
            <ReviewBoxComponent arrowDirection="left" order={showOrder} />
          </ReviewBoxList>
          <AppDownLoadButtonWrapper>
            {appDownloadButton}
          </AppDownLoadButtonWrapper>
        </Content>
      </Wrapper>
    );
  },
);

interface IReviewBoxProps {
  arrowDirection: "left" | "right";
  order: number;
}

const ReviewBoxComponent = ({ arrowDirection, order }: IReviewBoxProps) => {
  let MessageArrowElement = null;

  switch (arrowDirection) {
    case "right":
      MessageArrowElement = (
        <RightMessageArrowWrapper>
          <Icon
            icon={PACKS.APP_DOWNLOAD_BANNER.TALK_BOX_TRIANGLE}
            width={12}
            height={16}
          />
        </RightMessageArrowWrapper>
      );
      break;
    case "left":
      MessageArrowElement = (
        <LeftMessageArrowWrapper>
          <Icon
            icon={PACKS.APP_DOWNLOAD_BANNER.TALK_BOX_TRIANGLE}
            width={12}
            height={16}
          />
        </LeftMessageArrowWrapper>
      );
      break;
  }

  return (
    <ReviewBox>
      <ReviewHeader>
        <UserName>
          <FormattedMessage
            id={`post_footer_app_download_banner_user_${order}_${arrowDirection}`}
          />
        </UserName>
        <Icon
          icon={PACKS.APP_DOWNLOAD_BANNER.REVIEW_FIVE_STAR}
          width={30}
          height={6}
        />
      </ReviewHeader>
      <ReviewContent>
        <FormattedMessage
          id={`post_footer_app_download_banner_review_${order}_${arrowDirection}`}
        />
      </ReviewContent>
      {MessageArrowElement}
    </ReviewBox>
  );
};

export default AppDownLoadBanner;
